import { diffLines } from "diff";

export type DiffLine = {
  content: string;
  type: "added" | "removed" | "unchanged";
};

/** Normalize CRLF / lone CR so line boundaries match editors and GraphQL payloads. */
function normalizeNewlines(s: string | null): string {
  return (s ?? "").replace(/\r\n/g, "\n").replace(/\r/g, "\n");
}

/**
 * Split a diff chunk value into logical lines. Chunks from `diffLines` often end with `\n`
 * as a line terminator (not an extra empty line).
 */
function linesFromChunk(value: string): string[] {
  if (value === "") return [];
  const parts = value.split("\n");
  if (parts.length > 1 && parts[parts.length - 1] === "") {
    parts.pop();
  }
  return parts;
}

/**
 * Line-based diff for the publish / rollback preview. Uses the `diff` package (Myers)
 * instead of naive LCS-on-lines, so duplicate lines (e.g. `console.log('v2');` in both
 * old and new) align once as unchanged instead of spurious + and - pairs.
 */
export function computeDiff(
  oldText: string | null,
  newText: string | null
): DiffLine[] {
  const oldStr = normalizeNewlines(oldText);
  const newStr = normalizeNewlines(newText);
  const parts = diffLines(oldStr, newStr);
  const result: DiffLine[] = [];

  for (const part of parts) {
    const type: DiffLine["type"] = part.added
      ? "added"
      : part.removed
      ? "removed"
      : "unchanged";
    for (const line of linesFromChunk(part.value)) {
      result.push({ content: line, type });
    }
  }

  return result;
}
